import { Outlet } from "react-router";
import { useAppNavigate } from "../hooks/useAppNavigate";
import { useCallback, Suspense, useRef, useState, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/hooks";
import { toggleCart } from "../redux/slices/cartSlice";
import RestaurantGate from "../components/RestaurantGate";
import Cart from "../components/Cart";
import { useNavWidget } from "../hooks/useNavWidget";
import { useFooterWidget } from "../hooks/useFooterWidget";
import type { NavItem } from "../components/navigation";

interface ProtectedLayoutProps {
  className?: string;
  requiresRestaurant?: boolean;
  redirectTo?: string;
}

export default function ProtectedLayout({
  className = "",
  requiresRestaurant = true,
  redirectTo = "/select-location"
}: ProtectedLayoutProps = {}) {
  const appNavigate = useAppNavigate();
  const dispatch = useDispatch();
  const { isEditControl } = useAppSelector((state) => state.editControl);

  // Use widget hooks - this replaces all the complex widget management logic
  const nav = useNavWidget();
  const footer = useFooterWidget();

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  /**
   * Track header height dynamically (resizes + widget changes)
   */
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, [nav.HeaderComponent]);

  /**
   * Handlers
   */
  const handleNavClick = useCallback(
    (item: NavItem) => {
      if (typeof item.url === "string") {
        appNavigate(item.url);
      }
    },
    [appNavigate]
  );

  const handleLogoClick = useCallback(() => appNavigate("/"), [appNavigate]);
  const handleCartClick = useCallback(() => {
    dispatch(toggleCart());
  }, [dispatch]);

  return (
    <div className={className}>
      {/* Navigation Edit Icon - Only show when edit control is enabled and nav is not being edited */}
      {isEditControl && !nav.isNavEditMode && (
        <button
          onClick={nav.actions.toggleEditMode}
          disabled={nav.loading.updateLoading || nav.loading.fetchLoading}
          className="fixed left-1/2 transform -translate-x-1/2 z-60 px-4 py-2 rounded-full transition-colors flex items-center space-x-2"
          style={{ 
            backgroundColor: '#EE4445', 
            color: 'white',
            top: `${headerHeight - 18}px` // Position higher up with more overlap
          }}
          title="Edit navigation"
        >
          {/* Edit Icon (Clean Pencil) */}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          <span className="text-sm font-medium">Edit</span>
        </button>
      )}

      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50"
      >
        {nav.HeaderComponent && (
          <Suspense
            fallback={<div className="h-16 bg-white shadow-md animate-pulse" />}
          >
            <nav.HeaderComponent
              data={nav.headerData}
              onNavClick={handleNavClick}
              onLogoClick={handleLogoClick}
              onCartClick={handleCartClick}
              onUpdate={nav.actions.update}
              onSave={nav.actions.save}
              isEditing={nav.isNavEditMode}
            />
          </Suspense>
        )}
      </header>

      <main className="min-h-screen">
        <RestaurantGate
          requiresRestaurant={requiresRestaurant}
          redirectTo={redirectTo}
        >
          <Outlet />
        </RestaurantGate>

        <footer className="w-full relative">
          {footer.FooterComponent && (
            <Suspense
              fallback={<div className="h-32 bg-gray-800 animate-pulse" />}
            >
              <footer.FooterComponent
                data={footer.footerData}
                onUpdate={footer.actions.update}
                isEditing={footer.isEditMode}
              />
            </Suspense>
          )}
        </footer>
      </main>

      <Cart />
    </div>
  );
}
